import React, { FC, ReactNode, useCallback, useRef } from 'react'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import { cn } from 'ethcss'
import styles from './templateEditor__leftPane.jcss'

type TemplateEditorLeftPaneProps = {
    paneId: string
    title: string
    collapsed: boolean
    onToggle: () => void
    height?: number
    headerAction?: ReactNode
    children?: ReactNode
}

export const TemplateEditorLeftPane: FC<TemplateEditorLeftPaneProps> = ({
    paneId,
    title,
    collapsed,
    onToggle,
    height,
    headerAction,
    children,
}) => {
    const isFlexible = paneId === 'catalog'
    const style =
        collapsed || isFlexible || height == null
            ? undefined
            : ({
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: `${height}px`,
                  height: `${height}px`,
                  minHeight: `${height}px`,
                  maxHeight: `${height}px`,
              } as React.CSSProperties)

    return (
        <section
            className={cn(
                styles.pane,
                isFlexible && styles.paneCatalog,
                collapsed && styles.paneCollapsed,
                !collapsed && !isFlexible && height != null && styles.paneFixed
            )}
            data-pane={paneId}
            style={style}
        >
            <header className={styles.header}>
                <button
                    type="button"
                    className={cn(styles.toggle, collapsed && styles.toggleCollapsed)}
                    title={translate('templateEditorTogglePane')}
                    aria-expanded={!collapsed}
                    onClick={onToggle}
                >
                    <Icon type="arrow_down" size={16} />
                </button>
                <span className={styles.title}>{title}</span>
                {headerAction ? <div className={styles.headerAction}>{headerAction}</div> : null}
            </header>
            {!collapsed && children ? <div className={styles.body}>{children}</div> : null}
        </section>
    )
}

type SplitterProps = {
    onResize: (deltaY: number) => void
    disabled?: boolean
}

export const TemplateEditorPanelSplitter: FC<SplitterProps> = ({ onResize, disabled }) => {
    const lastYRef = useRef<number | null>(null)

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (disabled) {
                return
            }

            event.preventDefault()
            const target = event.currentTarget
            lastYRef.current = event.clientY
            target.setPointerCapture(event.pointerId)
            target.classList.add(styles.splitterActive)
        },
        [disabled]
    )

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (lastYRef.current == null) {
                return
            }

            const deltaY = event.clientY - lastYRef.current
            lastYRef.current = event.clientY
            if (deltaY !== 0) {
                onResize(deltaY)
            }
        },
        [onResize]
    )

    const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (lastYRef.current == null) {
            return
        }

        lastYRef.current = null
        event.currentTarget.classList.remove(styles.splitterActive)
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
        }
    }, [])

    return (
        <div
            className={cn(styles.splitter, disabled && styles.splitterDisabled)}
            role="separator"
            aria-orientation="horizontal"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
        />
    )
}
